import React, { useState, useEffect } from 'react';
import { Card, Table, ProgressBar } from 'react-bootstrap';
import moment from 'moment';
import hdBonusService from '../../services/hd.bonus.service';
import { hideLoading, showLoading } from '../../lib/uiService';
import { toast } from 'react-toastify';
import { useAuth } from '../../providers/AuthProvider';
import { UserRole } from '../../lib/types';
import Pagination from '../common/Pagination';

interface QuarterlyBonusSummaryProps {
  refreshTrigger?: number;
}

const QuarterlyBonusSummary: React.FC<QuarterlyBonusSummaryProps> = ({ refreshTrigger = 0 }) => {
  const [quarterlySummaries, setQuarterlySummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(0);

  // Use auth hook directly
  const { id, role, isCheckingLoggedState } = useAuth();
  const isAdmin = role && role.includes(UserRole.Admin);

  // Current quarter and year
  const currentYear = moment().year();
  const currentQuarter = Math.ceil((moment().month() + 1) / 3);

  // Calculate maximum potential for the quarter (3 months * $100 per month = $300)
  const maxQuarterlyBonus = 300;

  // Fetch quarterly summary data
  useEffect(() => {
    // Don't fetch if auth state is still loading
    if (isCheckingLoggedState) return;

    const fetchQuarterlySummaries = async () => {
      try {
        setLoading(true);
        showLoading();

        const { data } = await hdBonusService.GetQuarterlySummary({
          all: isAdmin,
        });

        // Sort the summaries by year and quarter in descending order
        const sortedSummaries = [...(data || [])].sort((a, b) => {
          return a.year * 100 + a.quarter - (b.year * 100 + b.quarter);
        });

        setQuarterlySummaries(sortedSummaries);

        // Calculate total pages for pagination
        setTotalPages(Math.ceil(sortedSummaries.length / itemsPerPage));

        hideLoading();
      } catch (error) {
        hideLoading();
        toast.error('Error loading quarterly summary data');
        console.error('Error fetching quarterly summaries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuarterlySummaries();
  }, [isAdmin, refreshTrigger, isCheckingLoggedState, itemsPerPage]);

  // Format to display quarter (e.g., "Q1 2023")
  const formatQuarter = (year, quarter) => `Q${quarter} ${year}`;

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return quarterlySummaries.slice(startIndex, endIndex);
  };

  // Calculate quarter progress based on year and quarter
  const getQuarterProgress = (year, quarter) => {
    if (year < currentYear || (year === currentYear && quarter < currentQuarter)) {
      // Past quarters are 100% complete
      return 100;
    } else if (year > currentYear || (year === currentYear && quarter > currentQuarter)) {
      // Future quarters are 0% complete
      return 0;
    } else {
      // Current quarter - calculate based on current month
      const currentMonthOfQuarter = (moment().month() % 3) + 1;
      return (currentMonthOfQuarter / 3) * 100;
    }
  };

  // Get variant for progress bar
  const getProgressVariant = (progress) => {
    if (progress < 30) return 'danger';
    if (progress < 70) return 'warning';
    return 'success';
  };

  // Calculate earnings progress
  const getEarningsProgress = (totalAmount) => {
    return (totalAmount / maxQuarterlyBonus) * 100;
  };

  // If still checking auth state, show nothing
  if (isCheckingLoggedState) return null;

  return (
    <Card className="mb-4">
      <Card.Header as="h5">Quarterly Bonus Accrual</Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <>
            <div className="small text-muted mb-3">Potential: $100/month x 3 months = $300/quarter</div>

            {/* Quarterly History Table */}
            {quarterlySummaries.length > 0 ? (
              <>
                <Table striped bordered hover responsive className="mb-3">
                  <thead>
                    <tr>
                      <th>Quarter</th>
                      <th>Bonus Accrual</th>
                      <th>Quarter Progress</th>
                      {isAdmin && <th>User</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentPageItems().map((summary) => {
                      const quarterProgress = getQuarterProgress(summary.year, summary.quarter);
                      const earningsProgress = getEarningsProgress(summary.totalAmount);

                      return (
                        <tr
                          key={summary.summaryId}
                          className={
                            summary.year === currentYear && summary.quarter === currentQuarter ? 'table-primary' : ''
                          }
                        >
                          <td>{formatQuarter(summary.year, summary.quarter)}</td>
                          <td>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span>
                                ${summary.totalAmount} of ${maxQuarterlyBonus}
                              </span>
                            </div>
                            <ProgressBar
                              now={earningsProgress}
                              variant={getProgressVariant(earningsProgress)}
                              style={{ height: '0.5rem' }}
                            />
                          </td>
                          <td>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span>{Math.round(quarterProgress)}%</span>
                            </div>
                            <ProgressBar
                              now={quarterProgress}
                              variant={getProgressVariant(quarterProgress)}
                              style={{ height: '0.5rem' }}
                            />
                          </td>
                          {isAdmin && (
                            <td>
                              {summary.user ? `${summary.user.firstName} ${summary.user.lastName}` : 'Unknown User'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {/* Pagination controls */}
                {totalPages > 1 && <Pagination total={totalPages} currentPage={currentPage} setPage={setCurrentPage} />}
              </>
            ) : (
              <div className="alert alert-info">No quarterly bonus data available yet.</div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default QuarterlyBonusSummary;
